import type React from 'react';

import type { IconName } from '#ui/MaterialIcon.js';

import { cn } from '@elara/client-core/lib/utils';

import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '#ui/tooltip.js';

interface DetailRowProps {
  icon?: IconName;
  /** Override the default accent icon colour (e.g. a file-type colour for a document row). */
  iconClassName?: string;
  trailingIcon?: IconName;
  /** A custom leading element (e.g. an avatar) shown in place of the icon. */
  leading?: React.ReactNode;
  value: React.ReactNode;
  /** Optional muted sub-label shown beneath the value (e.g. "Confirmation code"). */
  secondary?: React.ReactNode;
  /** When set, the whole row becomes a clickable action with a hover highlight. */
  onClick?: () => void;
  /** Hover tooltip describing the row's action (e.g. "View website", "Copy confirmation code"). */
  tooltip?: string;
  className?: string;
}

/**
 * A single icon + value row, styled after Google Maps' place panel: a coloured leading icon beside
 * a value that wraps freely with the icon pinned to the first line, an optional secondary sub-label,
 * and — when given an `onClick` — a clickable row with a hover highlight and an action tooltip.
 * Used across the expanded itinerary card so every field is presented the same way. Group several in
 * a `<div className="flex flex-col gap-1">`.
 */
export function DetailRow({
  icon,
  iconClassName,
  trailingIcon = 'chevron_right',
  leading,
  value,
  secondary,
  onClick,
  tooltip,
  className,
}: DetailRowProps) {
  const inner = (
    <>
      {leading ? (
        <div className="flex-shrink-0">{leading}</div>
      ) : (
        icon && (
          <MaterialIcon
            icon={icon}
            size={IconSizes.sm}
            className={cn('flex-shrink-0', iconClassName ?? 'text-primary')}
          />
        )
      )}
      <div className="min-w-0 break-words">
        <div>{value}</div>
        {secondary != null && <div className="text-xs text-muted-foreground">{secondary}</div>}
      </div>
    </>
  );

  if (!onClick) {
    return <div className="flex items-start gap-3 py-2.5">{inner}</div>;
  }

  // A `role="button"` div (not a real <button>) so the value can hold block content like
  // markdown notes, which are invalid inside a <button>.
  const row = (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
      className={cn(
        'group -mx-3 flex cursor-pointer items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-background-secondary',
        className,
      )}
    >
      <div className="flex-1 flex items-start gap-3">{inner}</div>
      <MaterialIcon
        icon={trailingIcon}
        className="flex-shrink-0 text-muted-foreground/30 opacity-0 group-hover:opacity-100 transition-colors"
      />
    </div>
  );

  if (!tooltip) return row;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{row}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
